import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { extractApiErrorMessage } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Review — Iteration 29
 * Public review-form landed at /review/:token. Customer rates 1-5 stars,
 * optional comment, then the review goes to the admin in `pending` status.
 */
const Review = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState("loading"); // loading | form | done | error
  const [ctx, setCtx] = useState({ user_name: "", context_label: "" });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/reviews/invite/${token}`);
        if (cancelled) return;
        setCtx(data);
        setStage("form");
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(extractApiErrorMessage(e) || "تعذّر تحميل النموذج");
        setStage("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    if (!rating) {
      toast.error("يرجى اختيار التقييم بالنجوم أولًا");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        token,
        rating,
        comment: comment.trim() || null,
      });
      setStage("done");
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر إرسال التقييم");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section
        className="max-w-xl mx-auto px-6 py-16 text-center"
        data-testid="review-page"
        dir="rtl"
      >
        {stage === "loading" && (
          <div className="py-20">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#0A4D3C]" />
            <p className="text-[#5E5C58] mt-4">جاري تحميل النموذج...</p>
          </div>
        )}

        {stage === "error" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 grid place-items-center">
              <AlertCircle className="w-8 h-8 text-rose-700" />
            </div>
            <h1 className="font-heading text-3xl text-[#222222] mt-6">تعذّر فتح الرابط</h1>
            <p className="text-[#5E5C58] mt-3" data-testid="review-error-msg">{errorMsg}</p>
            <Button
              onClick={() => navigate("/")}
              className="mt-8 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white px-8 h-11"
            >
              العودة للرئيسية
            </Button>
          </>
        )}

        {stage === "done" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="font-heading text-3xl text-[#222222] mt-6" data-testid="review-thanks-title">
              شكرًا لتقييمك
            </h1>
            <p className="text-[#5E5C58] mt-3 leading-relaxed">
              وصلَنا تقييمك بنجاح. سيتم نشره في صفحة الآراء بعد المراجعة.
              نقدّر وقتك ومشاركتك معنا 🌹
            </p>
            <Button
              onClick={() => navigate("/")}
              className="mt-8 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white px-8 h-11"
            >
              العودة للرئيسية
            </Button>
          </>
        )}

        {stage === "form" && (
          <>
            <h1 className="font-heading text-4xl text-[#222222]">رأيك يهمّنا</h1>
            <p className="text-[#5E5C58] mt-3 leading-relaxed">
              {ctx.user_name ? `مرحبًا ${ctx.user_name}،` : "مرحبًا،"}
              {" "}
              {ctx.context_label}
            </p>

            <div
              className="mt-8 flex justify-center gap-2"
              data-testid="review-stars"
              role="radiogroup"
              aria-label="التقييم بالنجوم"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-star-${n}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  aria-label={`${n} من 5`}
                  className="p-1 rounded-full transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-9 h-9 ${
                      (hover || rating) >= n
                        ? "fill-[#C5A059] text-[#C5A059]"
                        : "text-[#C9C2B5]"
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-xs text-[#5E5C58] mt-2" data-testid="review-rating-label">
              {rating ? `اخترتَ ${rating} من 5` : "اختر تقييمك بالنجوم"}
            </p>

            <div className="mt-8 text-start">
              <label
                htmlFor="review-comment"
                className="block text-sm text-[#222222] mb-2"
              >
                تعليقك <span className="text-[#8B8780]">(اختياري)</span>
              </label>
              <textarea
                id="review-comment"
                data-testid="review-comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="شاركنا تجربتك بكل أريحية... (يمكنك ترك الحقل فارغًا)"
                className="w-full rounded-xl border border-[#E5E1D8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#C5A059]"
              />
              <p
                className="mt-2 text-xs text-[#5E5C58] leading-relaxed"
                data-testid="review-privacy-note"
              >
                حرصًا على خصوصيتك، سيظهر تقييمك في الموقع باسمك الأول فقط.
              </p>
            </div>

            <Button
              data-testid="review-submit-btn"
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className="mt-6 w-full rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-11"
            >
              {submitting ? "جارٍ الإرسال..." : "إرسال التقييم"}
            </Button>
          </>
        )}
      </section>
    </PageShell>
  );
};

export default Review;
